import React, { useMemo } from "react";
import { MapPin, Navigation, Car } from "lucide-react";

/**
 * MockMap - SVG-based mock map for displaying coordinates as pins.
 * Coordinates are normalized between provided bounds and rendered visually.
 * Props:
 *  - markers: [{ id, lat, lng, type: 'pickup'|'dropoff'|'driver'|'passenger', label, color }]
 *  - className
 *  - showRoute: bool (draw line between pickup and dropoff)
 */
export const MockMap = ({ markers = [], routes = null, className = "", showRoute = true, height = "100%", onMarkerClick }) => {
  // Default bounds (somewhere downtown - normalized view)
  const bounds = useMemo(() => {
    if (markers.length === 0) {
      return { minLat: 51.22, maxLat: 51.32, minLng: -1.18, maxLng: -1.01 };
    }
    const lats = markers.map((m) => m.lat).filter((v) => v != null);
    const lngs = markers.map((m) => m.lng).filter((v) => v != null);
    let minLat = Math.min(...lats);
    let maxLat = Math.max(...lats);
    let minLng = Math.min(...lngs);
    let maxLng = Math.max(...lngs);
    const padLat = Math.max((maxLat - minLat) * 0.25, 0.01);
    const padLng = Math.max((maxLng - minLng) * 0.25, 0.01);
    return {
      minLat: minLat - padLat,
      maxLat: maxLat + padLat,
      minLng: minLng - padLng,
      maxLng: maxLng + padLng,
    };
  }, [markers]);

  const project = (lat, lng) => {
    const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * 100;
    const y = (1 - (lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * 100;
    return { x: `${x}%`, y: `${y}%` };
  };

  const pickup = markers.find((m) => m.type === "pickup");
  const dropoff = markers.find((m) => m.type === "dropoff");

  return (
    <div className={`map-grid-bg relative w-full ${className}`} style={{ height }} data-testid="mock-map">
      {/* Fake streets - diagonal "highways" */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
        <line x1="0%" y1="30%" x2="100%" y2="35%" stroke="rgba(10,10,10,0.08)" strokeWidth="14" />
        <line x1="0%" y1="70%" x2="100%" y2="65%" stroke="rgba(10,10,10,0.08)" strokeWidth="14" />
        <line x1="20%" y1="0%" x2="25%" y2="100%" stroke="rgba(10,10,10,0.08)" strokeWidth="10" />
        <line x1="75%" y1="0%" x2="80%" y2="100%" stroke="rgba(10,10,10,0.08)" strokeWidth="10" />
      </svg>

      {/* Route line — either the default pickup→dropoff dashed line, or an
          explicit `routes` payload (used by Admin's route-preview overlay). */}
      {routes && routes.length > 0 ? (
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {routes.map((r, idx) => {
            if (!r.points || r.points.length < 2) return null;
            const a = project(r.points[0].lat, r.points[0].lng);
            const b = project(r.points[r.points.length - 1].lat, r.points[r.points.length - 1].lng);
            return (
              <line
                key={r.id ?? `${a.x}-${a.y}-${b.x}-${b.y}-${r.color || "k"}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={r.color || "#0A0A0A"}
                strokeWidth={r.weight || 3}
                strokeDasharray={r.style === "dashed" ? "8,4" : "0"}
                strokeLinecap="round"
                strokeOpacity={r.opacity ?? 0.9}
              />
            );
          })}
        </svg>
      ) : showRoute && pickup && dropoff && (
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          <line
            x1={project(pickup.lat, pickup.lng).x}
            y1={project(pickup.lat, pickup.lng).y}
            x2={project(dropoff.lat, dropoff.lng).x}
            y2={project(dropoff.lat, dropoff.lng).y}
            stroke="#0A0A0A"
            strokeWidth="3"
            strokeDasharray="8,4"
            strokeLinecap="round"
          />
        </svg>
      )}

      {/* Markers */}
      {markers.map((m) => {
        if (m.lat == null || m.lng == null) return null;
        const { x, y } = project(m.lat, m.lng);
        return (
          <MapPin3D
            key={m.id}
            x={x}
            y={y}
            marker={m}
            onClick={onMarkerClick ? () => onMarkerClick(m) : undefined}
          />
        );
      })}

      {/* Cardinal indicator */}
      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm border border-border rounded-sm px-2 py-1 label-mini" data-testid="map-compass">
        N ↑
      </div>
    </div>
  );
};

const MapPin3D = ({ x, y, marker, onClick }) => {
  const { type, label, color } = marker;
  const isPickup = type === "pickup";
  const isDropoff = type === "dropoff";
  const isDriver = type === "driver";

  const bgColor =
    color ||
    (isPickup ? "#10B981" : isDropoff ? "#EF4444" : isDriver ? "#FFC107" : "#002FA7");
  const Icon = isDriver ? Car : isDropoff ? Navigation : MapPin;
  const shouldPulse = marker.pulse !== undefined ? marker.pulse : isDriver;

  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-full group cursor-pointer"
      style={{ left: x, top: y }}
      data-testid={`map-marker-${marker.id}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="relative flex flex-col items-center">
        <div
          className={`w-9 h-9 rounded-full border-2 border-black flex items-center justify-center shadow-lg transition-transform group-hover:scale-110 ${
            shouldPulse ? "pulse-active" : ""
          }`}
          style={{ backgroundColor: bgColor }}
        >
          <Icon className="w-4 h-4 text-black" strokeWidth={2.5} />
        </div>
        <div className="w-0.5 h-2 bg-black" />
        <div className="w-2 h-2 rounded-full bg-black" />
        {label && (
          <div className="absolute -top-9 whitespace-nowrap bg-black text-white px-2 py-1 text-xs rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none label-mini">
            {label}
          </div>
        )}
      </div>
    </div>
  );
};

export default MockMap;
